/**
 * Toggle.jsx
 *
 * A small a11y-correct switch component used for the system-wide
 * flags in /admin (public signup, plans tab visibility, etc.).
 *
 * RTL handling: with dir="rtl" on a parent, the visual "start" of
 * the track flips to the right, but Tailwind's translate-x-* classes
 * are physical (LTR direction only). To make the thumb appear on the
 * visually-active side in both LTR and RTL we negate the offset in
 * RTL. We use literal class strings (not template-interpolated ones)
 * so Tailwind's JIT actually emits the CSS for them.
 */
import { useLanguage } from "../../contexts/LanguageContext";
import { useDarkMode } from "../../hooks/useDarkMode";

export default function Toggle({
  checked,
  onChange,
  disabled = false,
  title,
  ariaLabel,
}) {
  const { isRTL } = useLanguage();
  const { darkMode } = useDarkMode();

  // Pre-resolved class strings — Tailwind needs these as literals
  // somewhere in the source to generate the CSS. Track is w-11 (44px),
  // thumb is w-5 (20px), so the active offset is 5 units (20px) which
  // leaves a 4px margin on the active side — visually centered.
  let thumbPos;
  if (isRTL) {
    thumbPos = checked ? "-translate-x-5" : "translate-x-0";
  } else {
    thumbPos = checked ? "translate-x-5"  : "translate-x-0";
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={onChange}
      disabled={disabled}
      title={title}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? "bg-blue-600" : darkMode ? "bg-gray-700" : "bg-gray-300"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${thumbPos}`}
      />
    </button>
  );
}